import { useEffect, useState } from "react";
import { Download, Wallet, PiggyBank, ReceiptText, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah, BULAN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function toYM(m, y) {
  return `${y}-${String(m).padStart(2, "0")}`;
}

export default function Reports() {
  const { user, schoolId } = useAuth();
  const now = new Date();
  const startDefault = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const [startMonth, setStartMonth] = useState(String(startDefault.getMonth() + 1));
  const [startYear, setStartYear] = useState(String(startDefault.getFullYear()));
  const [endMonth, setEndMonth] = useState(String(now.getMonth() + 1));
  const [endYear, setEndYear] = useState(String(now.getFullYear()));
  const [data, setData] = useState(null);
  const [section, setSection] = useState("semua");
  const [needsSchool, setNeedsSchool] = useState(false);
  const [exporting, setExporting] = useState(false);

  const start = toYM(startMonth, startYear);
  const end = toYM(endMonth, endYear);

  const load = () => {
    setNeedsSchool(false);
    setData(null);
    api.get(`/reports/summary?start=${start}&end=${end}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 400 && e.response?.data?.detail?.includes("sekolah")) { setNeedsSchool(true); }
        else { setData({ months: [], totals: {}, error: true }); toast.error(apiError(e)); }
      });
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setNeedsSchool(true); return; }
    load();
  }, [start, end, schoolId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/reports/export?start=${start}&end=${end}&section=${section}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan_keuangan_${section}_${start}_sd_${end}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan Excel berhasil diunduh");
    } catch (err) {
      toast.error("Gagal mengunduh laporan");
    } finally {
      setExporting(false);
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="reports-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const cards = data ? [
    { label: "Pemasukan SPP", value: data.totals.spp, icon: Wallet, tone: "bg-blue-50 text-blue-600", testId: "report-total-spp" },
    { label: "Pemasukan Tagihan", value: data.totals.tagihan, icon: ReceiptText, tone: "bg-slate-100 text-slate-600", testId: "report-total-tagihan" },
    { label: "Pemasukan Lainnya", value: data.totals.lainnya, icon: Wallet, tone: "bg-teal-50 text-teal-600", testId: "report-total-lainnya" },
    { label: "Setoran Tabungan", value: data.totals.setor, icon: PiggyBank, tone: "bg-emerald-50 text-emerald-600", testId: "report-total-setor" },
    { label: "Penarikan Tabungan", value: data.totals.tarik, icon: PiggyBank, tone: "bg-amber-50 text-amber-600", testId: "report-total-tarik" },
    { label: "Belanja Sekolah", value: data.totals.belanja, icon: ShoppingCart, tone: "bg-rose-50 text-rose-600", testId: "report-total-belanja" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Laporan Keuangan</h1>
          <p className="mt-1 text-sm text-slate-500">Rekap pemasukan dan tabungan per periode.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="w-[150px]" data-testid="report-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua" data-testid="report-section-semua">Semua Bagian</SelectItem>
              <SelectItem value="spp" data-testid="report-section-spp">SPP Saja</SelectItem>
              <SelectItem value="tabungan" data-testid="report-section-tabungan">Tabungan Saja</SelectItem>
              <SelectItem value="tagihan" data-testid="report-section-tagihan">Tagihan Saja</SelectItem>
              <SelectItem value="kas" data-testid="report-section-kas">Kas & Belanja</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} disabled={exporting || !data} data-testid="report-export-button" variant="outline" className="gap-2 rounded-md">
            <Download className="h-4 w-4" strokeWidth={1.5} /> {exporting ? "Mengunduh..." : "Unduh Excel"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="space-y-1.5">
          <Label>Dari Bulan</Label>
          <Select value={startMonth} onValueChange={setStartMonth}>
            <SelectTrigger className="w-[140px]" data-testid="report-start-month"><SelectValue /></SelectTrigger>
            <SelectContent>{BULAN.map((b, i) => <SelectItem key={i + 1} value={String(i + 1)}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Tahun</Label>
          <Select value={startYear} onValueChange={setStartYear}>
            <SelectTrigger className="w-[100px]" data-testid="report-start-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Sampai Bulan</Label>
          <Select value={endMonth} onValueChange={setEndMonth}>
            <SelectTrigger className="w-[140px]" data-testid="report-end-month"><SelectValue /></SelectTrigger>
            <SelectContent>{BULAN.map((b, i) => <SelectItem key={i + 1} value={String(i + 1)}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Tahun</Label>
          <Select value={endYear} onValueChange={setEndYear}>
            <SelectTrigger className="w-[100px]" data-testid="report-end-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {data && (
          <div className="ml-auto grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5" data-testid="report-saldo-tabungan-badge">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600">Saldo Tabungan</p>
              <p className="font-heading text-lg font-bold text-emerald-700 tabular-nums">{formatRupiah(data.saldo_tabungan)}</p>
            </div>
            <div className="rounded-lg bg-[#0060AF] px-4 py-2.5 text-white" data-testid="report-saldo-kas-badge">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-200">Saldo Kas Sekolah</p>
              <p className="font-heading text-lg font-bold tabular-nums">{formatRupiah(data.saldo_kas)}</p>
            </div>
          </div>
        )}
      </div>

      {!data && <div className="py-16 text-center text-sm text-slate-500" data-testid="reports-loading">Memuat laporan...</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((c) => (
              <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={c.testId}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${c.tone}`}>
                    <c.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="mt-3 font-heading text-xl font-bold tracking-tight text-slate-900">{formatRupiah(c.value)}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="font-heading text-base font-semibold text-slate-800">Rincian per Bulan</h3>
            <div className="mt-4 overflow-x-auto">
              <Table data-testid="report-monthly-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Bulan</TableHead>
                    <TableHead className="text-right">SPP</TableHead>
                    <TableHead className="text-right">Setoran</TableHead>
                    <TableHead className="text-right">Penarikan</TableHead>
                    <TableHead className="text-right">Tagihan</TableHead>
                    <TableHead className="text-right">Lainnya</TableHead>
                    <TableHead className="text-right">Belanja</TableHead>
                    <TableHead className="text-right">Kas Bersih</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.months.map((m) => (
                    <TableRow key={m.label} className="transition-colors duration-200 hover:bg-slate-50">
                      <TableCell className="text-sm font-medium text-slate-800">{m.label}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{formatRupiah(m.spp)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{formatRupiah(m.setor)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{formatRupiah(m.tarik)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{formatRupiah(m.tagihan)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{formatRupiah(m.lainnya)}</TableCell>
                      <TableCell className="text-right text-sm text-rose-600">{formatRupiah(m.belanja)}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-slate-900">{formatRupiah(m.spp + m.tagihan + m.lainnya - m.belanja)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
